import { CompanyFormData, CompanyThemeSettings } from 'shared/types';
import type { ApiResponse } from 'shared/types/api';
import api, { clearCache, cachedGet } from './apiClient';

// Cache TTL constants (in milliseconds)
const CACHE_SHORT = 30 * 1000;  // 30 seconds
const CACHE_MEDIUM = 60 * 1000; // 1 minute

interface CompaniesListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  industry?: string;
  companyType?: 'cliente' | 'proveedor';
  userCountMin?: number;
  userCountMax?: number;
  missingSafetyStaff?: boolean;
  accountExecutiveId?: string;
  accountExecutiveRole?: 'titular' | 'suplente';
  includeAccountExecutive?: boolean;
}

export interface SafetyStaffCoverageRow {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  titularCount: number;
  suplenteCount: number;
  totalCount: number;
}

export interface SafetyStaffStatus {
  /** Whether the company has an effective Soporte Monitor assigned. */
  assigned: boolean;
  /** Whether the company is blocked from creating content (= !assigned). */
  gated: boolean;
  requestedAt: string | null;
  lastAlertAt: string | null;
}

export interface SafetyStaffRequestResult extends ApiResponse<{ lastAlertAt: string }> {
  alreadyNotified?: boolean;
}

interface CompanyUsersParams {
  role?: string;
  isActive?: boolean;
}

interface CompanyContractorsParams {
  page?: number;
  limit?: number;
  status?: string;
}

interface CompanyReportsParams {
  reportType: 'contractors' | 'users';
  startDate?: string;
  endDate?: string;
}

interface BulkUpdateData {
  companyIds: string[];
  operation: 'activate' | 'deactivate' | 'updateSettings';
  data?: any;
}

export interface TwoFactorSettingsData {
  enabled: boolean;
  requiredRoles: string[];
  allowedMethods: ('authenticator' | 'email')[];
  enforceStrongestMethod: boolean;
  gracePeriodDays: number;
}

export interface TwoFactorComplianceUser {
  userId: string;
  name: string;
  email: string;
  role: string;
  totpEnabled: boolean;
  isCompliant: boolean;
}

export interface TwoFactorComplianceData {
  companyName: string;
  policyEnabled: boolean;
  requiredRoles?: string[];
  allowedMethods?: string[];
  enforceStrongestMethod?: boolean;
  compliance: {
    total: number;
    compliant: number;
    nonCompliant: number;
    complianceRate: number;
  };
  users: TwoFactorComplianceUser[];
}

// Raw user data from /2fa-needs-setup endpoint
export interface UserNeedingSetupRaw {
  userId: string;
  email: string;
  name: string;
  role: string;
  gracePeriodExpired: boolean;
  gracePeriodRemaining: number | null;
  canLogin: boolean;
}

// Normalized user data for UI components
export interface UserNeedingSetup {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isLockedOut: boolean;
  daysRemaining?: number;
}

export type UsersNeedingSetupResponse = ApiResponse<{
  companyName: string;
  policyEnabled: boolean;
  requiredRoles: string[];
  allowedMethods: string[];
  totalNeedingSetup: number;
  expiredGracePeriod: number;
  users: UserNeedingSetupRaw[];
}>;

export interface PadronEntryLite {
  cedula: string;
  name: string;
  type: 'fisica' | 'juridica' | 'dimex' | 'nite' | 'other';
  regime: string;
  status: string;
  isFullyActive: boolean;
  isMoroso: boolean;
  isOmiso: boolean;
  warning?: string;
  primaryActivity?: { code: string; description: string };
}

export const companyApi = {
  // Crear una empresa
  create: async (data: CompanyFormData) => {
    const result = await api.post('/companies', data);
    clearCache('/companies'); // Invalidate company list cache
    return result;
  },

  /**
   * Validate a cédula against Hacienda's Padrón Tributario.
   * Returns the parsed entry when registered, null when not, throws on
   * service outage. No caching here — the BE caches for 24h.
   */
  validateCedula: async (cedula: string): Promise<PadronEntryLite | null> => {
    const res = await api.get<{
      success: boolean;
      data: { registered: boolean; entry: PadronEntryLite | null };
    }>('/companies/validate-cedula', { cedula });
    return res?.data?.entry || null;
  },

  // Obtener lista de empresas (with short cache to prevent duplicate calls)
  getAll: async (params?: CompaniesListParams) => {
    return cachedGet('/companies', params, CACHE_SHORT);
  },

  // Obtener empresas para select/dropdown (cached longer since it changes less)
  getSelect: async (params?: { companyType?: 'cliente' | 'proveedor'; scope?: 'all' }) => {
    return cachedGet('/companies/select', params, CACHE_MEDIUM);
  },

  // Obtener una empresa por ID (cached)
  getById: async (id: string) => {
    return cachedGet(`/companies/${id}`, undefined, CACHE_SHORT);
  },

  // Estado del gate de Soporte Monitor (safety_staff). `gated` indica que la
  // empresa todavía no puede registrar usuarios ni crear contenido.
  getSafetyStaffStatus: async (id: string) => {
    return api.get<{ success: boolean; data: SafetyStaffStatus }>(
      `/companies/${id}/safety-staff-status`,
    );
  },

  // Solicitar asignación de un Soporte Monitor (notifica al Super Admin).
  requestSafetyStaff: async (id: string) => {
    const result = await api.post<SafetyStaffRequestResult>(
      `/companies/${id}/request-safety-staff`,
      {},
    );
    return result;
  },

  // IDs de empresas sin Soporte Monitor efectivo asignado (super_admin only).
  getGatedIds: async (): Promise<string[]> => {
    const res = await api.get<{ success: boolean; data: string[] }>('/companies/gated-ids');
    return res.data || [];
  },

  // Empresas por Soporte Monitor (titular/suplente), incluye 0/0 (super_admin only).
  getSafetyStaffCoverage: async (): Promise<SafetyStaffCoverageRow[]> => {
    const res = await api.get<{ success: boolean; data: SafetyStaffCoverageRow[] }>(
      '/companies/safety-staff-coverage',
    );
    return res.data || [];
  },

  // Obtener empresas supervisadas por el usuario actual
  getSupervisedCompanies: async () => {
    return api.get('/companies/supervised');
  },

  // Actualizar una empresa
  update: async (id: string, data: Partial<CompanyFormData>) => {
    const result = await api.put(`/companies/${id}`, data);
    clearCache('/companies'); // Invalidate caches
    return result;
  },

  // Actualizar estado de empresa
  updateStatus: async (id: string, status: string) => {
    const result = await api.patch(`/companies/${id}/status`, { status });
    clearCache('/companies');
    return result;
  },

  // Eliminar/Desactivar empresa
  delete: async (id: string) => {
    const result = await api.delete(`/companies/${id}`);
    clearCache('/companies');
    return result;
  },

  // super_admin only — permanently deletes the company (not a soft
  // deactivate). Only succeeds when the company has no active users besides
  // company_admin(s) and no pending invitations.
  permanentDelete: async (id: string, reason: string) => {
    const result = await api.delete(`/companies/${id}/permanent`, { data: { reason } });
    clearCache('/companies');
    return result;
  },

  // super_admin only — flips companyType between cliente/proveedor. Blocked
  // while the company has any active CompanyRelationship.
  swapType: async (id: string) => {
    const result = await api.post(`/companies/${id}/swap-type`, {});
    clearCache('/companies');
    clearCache(`/companies/${id}`);
    return result;
  },

  // Actualizar configuración de empresa
  updateSettings: async (id: string, settings: any) => {
    const result = await api.put(`/companies/${id}/settings`, settings);
    clearCache(`/companies/${id}`);
    return result;
  },

  // Obtener dashboard de empresa (cached)
  getDashboard: async (id: string) => {
    return cachedGet(`/companies/${id}/dashboard`, undefined, CACHE_SHORT);
  },

  // Contacto (email/phone) del ejecutivo de cuenta / Soporte Monitor — separado
  // del dashboard porque ese endpoint solo populatea firstName/lastName (PII
  // fuera de una caché compartida entre roles).
  getAccountExecutiveContact: async (id: string) => {
    return cachedGet(
      `/companies/${id}/account-executive-contact`,
      undefined,
      CACHE_SHORT,
    );
  },

  // Obtener usuarios de una empresa (cached)
  getUsers: async (id: string, params?: CompanyUsersParams) => {
    return cachedGet(`/companies/${id}/users`, params, CACHE_SHORT);
  },

  // Obtener contratistas de una empresa (cached)
  getContractors: async (id: string, params?: CompanyContractorsParams) => {
    return cachedGet(`/companies/${id}/contractors`, params, CACHE_SHORT);
  },

  // Obtener estadísticas de empresa (cached)
  getStats: async (id: string) => {
    return cachedGet(`/companies/${id}/stats`, undefined, CACHE_SHORT);
  },

  // Obtener estadísticas generales (cached)
  getGeneralStats: async () => {
    return cachedGet('/companies/stats', undefined, CACHE_SHORT);
  },

  // Obtener certificaciones por vencer
  getExpiringCertifications: async (params?: { daysAhead?: number; companyId?: string }) => {
    return cachedGet('/companies/certifications/expiring', params, CACHE_SHORT);
  },

  // Verificar si tiene contratistas activos
  hasActiveContractors: async (id: string) => {
    return cachedGet(`/companies/${id}/has-active-contractors`, undefined, CACHE_SHORT);
  },

  // Generar reportes de empresa
  getReports: async (id: string, params: CompanyReportsParams) => {
    return api.get(`/companies/${id}/reports`, params);
  },

  // Actualización masiva de empresas
  bulkUpdate: async (data: BulkUpdateData) => {
    const result = await api.post('/companies/bulk-update', data);
    clearCache('/companies');
    return result;
  },

  // ==================== TWO-FACTOR AUTHENTICATION ====================

  // Obtener configuración de 2FA de una empresa
  getTwoFactorSettings: async (companyId: string) => {
    return cachedGet(`/companies/${companyId}/2fa-settings`, undefined, CACHE_SHORT);
  },

  // Actualizar configuración de 2FA de una empresa
  updateTwoFactorSettings: async (companyId: string, settings: TwoFactorSettingsData) => {
    const result = await api.put(`/companies/${companyId}/2fa-settings`, settings);
    clearCache(`/companies/${companyId}`);
    return result;
  },

  // Obtener reporte de cumplimiento de 2FA
  getTwoFactorCompliance: async (companyId: string) => {
    return cachedGet(`/companies/${companyId}/2fa-compliance`, undefined, CACHE_SHORT);
  },

  // Obtener usuarios que necesitan configurar 2FA
  getUsersNeedingSetup: async (companyId: string) => {
    return cachedGet(`/companies/${companyId}/2fa-needs-setup`, undefined, CACHE_SHORT);
  },

  // ==================== DANGEROUS TASK COURSES ====================

  // Get dangerous task courses for a company
  getDangerousTaskCourses: async (companyId: string) => {
    return cachedGet(`/companies/${companyId}/dangerous-task-courses`, undefined, CACHE_SHORT);
  },

  // Update dangerous task courses for a company
  updateDangerousTaskCourses: async (companyId: string, dangerousTaskCourses: Array<{ courseId: string; courseName: string }>) => {
    const result = await api.put(`/companies/${companyId}/dangerous-task-courses`, { dangerousTaskCourses });
    clearCache(`/companies/${companyId}/dangerous-task-courses`);
    clearCache(`/companies/${companyId}`);
    return result;
  },

  // ==================== THEME SETTINGS ====================

  // Obtener tema de empresa (público, para login page)
  getThemePublic: async (companyId: string) => {
    return cachedGet(`/companies/${companyId}/theme/public`, undefined, CACHE_MEDIUM);
  },

  // Obtener tema de empresa (autenticado)
  getTheme: async (companyId: string) => {
    return cachedGet(`/companies/${companyId}/theme`, undefined, CACHE_SHORT);
  },

  // Obtener temas por defecto
  getDefaultThemes: async () => {
    return cachedGet('/companies/themes/defaults', undefined, CACHE_MEDIUM);
  },

  // Actualizar tema de empresa
  updateTheme: async (companyId: string, theme: Partial<CompanyThemeSettings>) => {
    const result = await api.put(`/companies/${companyId}/theme`, theme);
    clearCache(`/companies/${companyId}/theme`);
    clearCache(`/companies/${companyId}/theme/public`);
    return result;
  },

  // Resetear tema a valores por defecto
  resetTheme: async (companyId: string) => {
    const result = await api.delete(`/companies/${companyId}/theme`);
    clearCache(`/companies/${companyId}/theme`);
    clearCache(`/companies/${companyId}/theme/public`);
    return result;
  },

  // ==================== LOGO UPLOAD ====================

  // Get presigned URL for logo upload
  getLogoUploadUrl: async (companyId: string, contentType: string) => {
    return api.post(`/companies/${companyId}/theme/logo/upload`, { contentType });
  },

  // Confirm logo upload after S3 upload completes
  confirmLogoUpload: async (companyId: string, key: string) => {
    const result = await api.post(`/companies/${companyId}/theme/logo/confirm`, { key });
    clearCache(`/companies/${companyId}/theme`);
    clearCache(`/companies/${companyId}/theme/public`);
    return result;
  },

  // Delete company logo
  deleteLogo: async (companyId: string) => {
    const result = await api.delete(`/companies/${companyId}/theme/logo`);
    clearCache(`/companies/${companyId}/theme`);
    clearCache(`/companies/${companyId}/theme/public`);
    return result;
  },

  // Get presigned download URL for company logo (public endpoint).
  // Presigned URLs are valid for 15+ minutes — cache so re-mounts of
  // Sidebar/CompanyDetail don't refire the lookup on every route change.
  getLogoDownloadUrl: async (companyId: string) => {
    return api.get(`/companies/${companyId}/theme/logo/download`, undefined, {
      skipAuth: true,
      cacheTTL: 5 * 60 * 1000,
    });
  },

  // ==================== COMPANY RELATIONSHIPS ====================

  // Get proveedores linked to a client company. `tags` accepts a
  // comma-separated list of marketplace tag IDs; the response includes an
  // `activeExternalEmployees` count per provider, scoped to this client.
  // `sortBy=activeEmployees` sorts by that count (asc/desc).
  //
  // Deliberately NOT cached (was cachedGet with CACHE_SHORT until
  // 2026-08-31): `activeExternalEmployees` and `userAllocation` here change
  // from OTHER sessions — a provider confirming a pending assignment, or a
  // different client_admin tab adjusting cupo — that this session's cache
  // has no way to know about. A stale 30s-cached read showed a lower
  // employee/cupo count than what had already been confirmed elsewhere,
  // which looked like data corruption but was purely a stale read.
  getProveedores: async (
    companyId: string,
    params?: {
      page?: number;
      limit?: number;
      status?: string;
      search?: string;
      tags?: string;
      sortBy?: 'createdAt' | 'activeEmployees';
      sortDir?: 'asc' | 'desc';
    },
  ) => {
    return api.get(`/companies/${companyId}/proveedores`, params);
  },

  // Detailed summary of a single provider's activity inside this client.
  // Returns provider info, active external users at this client, and
  // counts of service cards / permits / evaluations grouped by status.
  // Not cached — same cross-session staleness reasoning as getProveedores
  // above; this is the detail-panel counterpart of that same list.
  getProviderInClientSummary: async (clientId: string, providerId: string) => {
    return api.get(`/companies/${clientId}/proveedores/${providerId}/summary`);
  },

  // Get clients linked to a proveedor company
  getClients: async (companyId: string, params?: { page?: number; limit?: number; status?: string; search?: string; scope?: 'verifications' }) => {
    return cachedGet(`/companies/${companyId}/clients`, params, CACHE_SHORT);
  },

  // Create a relationship between a client and a proveedor
  createRelationship: async (clientCompanyId: string, data: { proveedorCompanyId: string; notes?: string; startDate?: string }) => {
    const result = await api.post(`/companies/${clientCompanyId}/proveedores`, data);
    clearCache(`/companies/${clientCompanyId}`);
    return result;
  },

  // Update a company relationship
  updateRelationship: async (relationshipId: string, data: { status?: string; notes?: string; endDate?: string }) => {
    const result = await api.put(`/companies/company-relationships/${relationshipId}`, data);
    clearCache('/companies');
    return result;
  },

  // Soft-delete a company relationship
  deleteRelationship: async (relationshipId: string) => {
    const result = await api.delete(`/companies/company-relationships/${relationshipId}`);
    clearCache('/companies');
    return result;
  },
};
